import { serialiseValue } from '@/features/flags/flag-evaluation-telemetry';
import {
  BaseEvaluationStrategy,
  ErrorCode,
  type EvaluationContext,
  type FinalResult,
  FlagNotFoundError,
  type FlagValue,
  MultiProvider,
  type Provider,
  type ProviderEntryInput,
  type ProviderResolutionResult,
  type ProviderResolutionSuccessResult,
  ProviderStatus,
  type StrategyEvaluationContext,
} from '@openfeature/web-sdk';
import { isEqual } from 'lodash-es';

type ProviderResult<T extends FlagValue> = ProviderResolutionResult<T>;
type HealthyResult<T extends FlagValue> = ProviderResolutionSuccessResult<T>;

// ensures only one or two providers can be passed into this strategy
export function createCustomComparisonMultiProvider(options: {
  primary: ProviderEntryInput<Provider>;
  secondary?: ProviderEntryInput<Provider>;
}): MultiProvider {
  const { primary, secondary } = options;
  const entries = secondary ? [primary, secondary] : [primary];
  const provider = new MultiProvider(entries, new CustomComparisonStrategy());
  provider.metadata = { ...provider.metadata, name: 'ComparisonProvider' };
  Object.defineProperty(provider, 'flagsmithClient', {
    get: () => (primary.provider as { flagsmithClient?: unknown }).flagsmithClient,
  });
  return provider;
}

class CustomComparisonStrategy extends BaseEvaluationStrategy<ProviderStatus, Provider> {
  override runMode = 'parallel' as const;

  constructor() {
    super(ProviderStatus);
  }

  override determineFinalResult<T extends FlagValue>(
    _strategyContext: StrategyEvaluationContext,
    _context: EvaluationContext,
    providerResults: ProviderResult<T>[]
  ): FinalResult<T> {
    const [primary, secondary] = providerResults;

    if (this.isHealthy(primary) && this.isHealthy(secondary)) {
      const mismatch = !this.valuesEqual(primary.details.value, secondary.details.value);
      const reasonDiverged = primary.details.reason !== secondary.details.reason;

      // Agreement reports nothing; the total action count is the denominator.
      return this.withMetadata(primary, {
        mismatch: mismatch || undefined,
        shadowValue: mismatch ? serialiseValue(secondary.details.value) : undefined,
        shadowReason: mismatch || reasonDiverged ? secondary.details.reason : undefined,
      });
    }

    if (
      this.isHealthy(primary) &&
      this.errorCodeOf(secondary) === ErrorCode.PROVIDER_NOT_READY
    ) {
      return this.withMetadata(primary, { shadowPending: true });
    }

    if (this.isHealthy(primary)) {
      return this.withMetadata(primary, {
        failedProvider: secondary?.providerName,
        failedCode: this.errorCodeOf(secondary),
      });
    }

    if (this.isHealthy(secondary)) {
      return this.withMetadata(secondary, {
        failedProvider: primary?.providerName,
        failedCode: this.errorCodeOf(primary),
      });
    }

    if (this.isFlagMissing(primary) && (!secondary || this.isFlagMissing(secondary))) {
      throw new FlagNotFoundError();
    }

    return this.collectProviderErrors(providerResults);
  }

  protected valuesEqual(a: FlagValue, b: FlagValue): boolean {
    return isEqual(a, b);
  }

  private isHealthy<T extends FlagValue>(
    result: ProviderResult<T> | undefined
  ): result is HealthyResult<T> {
    return result !== undefined && !this.hasError(result);
  }

  private isFlagMissing<T extends FlagValue>(
    result: ProviderResult<T> | undefined
  ): boolean {
    return (
      result !== undefined && this.hasErrorWithCode(result, ErrorCode.FLAG_NOT_FOUND)
    );
  }

  // No `thrownError` branch needed: MultiProvider normalises a throw into this
  // code before the strategy sees it.
  private errorCodeOf<T extends FlagValue>(
    result: ProviderResult<T> | undefined
  ): string | undefined {
    return (result as { details?: { errorCode?: ErrorCode } } | undefined)?.details
      ?.errorCode;
  }

  private withMetadata<T extends FlagValue>(
    winner: HealthyResult<T>,
    outcome: {
      mismatch?: boolean;
      failedProvider?: string;
      failedCode?: string;
      shadowValue?: string;
      shadowReason?: string;
      shadowPending?: boolean;
    }
  ): FinalResult<T> {
    const findings = Object.fromEntries(
      Object.entries(outcome).filter(([, finding]) => finding !== undefined)
    );

    return {
      provider: winner.provider,
      providerName: winner.providerName,
      details: {
        ...winner.details,
        flagMetadata: {
          ...winner.details.flagMetadata,
          ...findings,
          provider: winner.providerName,
        },
      },
    };
  }
}
